import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useCardano } from "@cardano-foundation/cardano-connect-with-wallet";
import { CARDANO_NETWORK } from "@constants/config";
import { NetworkType } from "@cardano-foundation/cardano-connect-with-wallet-core";
import { Icon } from "@ui/icons/icon";
import { ICON_COLLECTION } from "@ui/icons/icon-list";
import { useTheme } from "@/components/theme-context/theme-context";
import { ActualFundType } from "@/constants/funds-data";
import { fetchTokenList } from "@/api/create-fund";
import { formatTokenAmount } from "@/helpers/token-amount";

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeposit: (multiple: number) => Promise<void>;
  fundId: string;
  fund: ActualFundType;
}

export const DepositModal = ({
  isOpen,
  onClose,
  onDeposit,
  fundId,
  fund,
}: DepositModalProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { enabledWallet } = useCardano({ limitNetwork: CARDANO_NETWORK });
  const { data: tokenList } = useQuery({
    queryKey: ["token-list"],
    queryFn: fetchTokenList,
  });

  const [multiple, setMultiple] = useState<string>("");
  const [balance, setBalance] = useState<number | null>(null);
  const [tokenBalances, setTokenBalances] = useState<Map<string, bigint>>(
    new Map()
  );
  const [loading, setLoading] = useState(false);

  const tokenDecimalsMap = useMemo(() => {
    const map = new Map<string, number>();
    (tokenList ?? []).forEach((token) => {
      if (!token.token_id) return;
      map.set(token.token_id.toLowerCase(), token.token_decimal_places ?? 0);
    });
    return map;
  }, [tokenList]);

  const getTokenDecimals = (tokenId: string) =>
    tokenDecimalsMap.get(tokenId.toLowerCase()) ?? 0;

  // Fetch wallet balance and token balances from UTXOs
  useEffect(() => {
    const fetchBalances = async () => {
      if (!enabledWallet || !("cardano" in window)) {
        return;
      }

      try {
        const cardano = (window as any).cardano;
        const walletAPI = await cardano[enabledWallet].enable();
        const utxosHex: string[] = await walletAPI.getUtxos();

        const CSL = await import("@emurgo/cardano-serialization-lib-asmjs");

        const hexToBytes = (hex: string) => {
          const bytes = new Uint8Array(hex.length / 2);
          for (let i = 0; i < hex.length; i += 2) {
            bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
          }
          return bytes;
        };

        let totalLovelace = BigInt(0);
        const tokens = new Map<string, bigint>();

        for (const utxoHex of utxosHex) {
          const utxo = CSL.TransactionUnspentOutput.from_bytes(
            hexToBytes(utxoHex)
          );
          const output = utxo.output();
          const amount = output.amount();

          totalLovelace += BigInt(amount.coin().to_str());

          const multiasset = amount.multiasset();
          if (multiasset) {
            const policyIds = multiasset.keys();
            for (let i = 0; i < policyIds.len(); i++) {
              const policyId = policyIds.get(i);
              const assets = multiasset.get(policyId);
              if (!assets) continue;

              const assetNames = assets.keys();
              for (let j = 0; j < assetNames.len(); j++) {
                const assetName = assetNames.get(j);
                const qty = assets.get(assetName);
                if (!qty) continue;

                const nameBytes = assetName.name();
                let nameHex = "";
                for (let k = 0; k < nameBytes.length; k++) {
                  nameHex += nameBytes[k].toString(16).padStart(2, "0");
                }
                const tokenId = (policyId.to_hex() + nameHex).toLowerCase();
                const existing = tokens.get(tokenId) ?? BigInt(0);
                tokens.set(tokenId, existing + BigInt(qty.to_str()));
              }
            }
          }
        }

        setBalance(Number(totalLovelace) / 1_000_000);
        setTokenBalances(tokens);
      } catch (error) {
        console.error("Failed to fetch balances:", error);
      }
    };

    if (isOpen) {
      fetchBalances();
    }
  }, [isOpen, enabledWallet]);

  // Compute required amounts per token
  const requiredAmounts = useMemo(() => {
    const multipleNum = parseInt(multiple, 10);
    if (isNaN(multipleNum) || multipleNum < 1) return null;

    return fund.tokens.map((_, index) => {
      return BigInt(fund.token_factors[index]) * BigInt(multipleNum) * BigInt(fund.fund_factor);
    });
  }, [multiple, fund]);

  const shareSummary = useMemo(() => {
    const multipleNum = parseInt(multiple, 10);
    if (isNaN(multipleNum) || multipleNum < 1) return null;
    const multipleBig = BigInt(multipleNum);
    const fundFactor = BigInt(fund.fund_factor);
    const royaltyFactor = BigInt(fund.royalty_factor);
    const mintedShares = fundFactor * multipleBig;
    const feeShares = royaltyFactor * multipleBig;
    const netShares = mintedShares - feeShares;
    return {
      mintedShares,
      feeShares,
      netShares,
    };
  }, [multiple, fund]);

  // Decode token name from hex (browser-safe, no Buffer needed)
  const decodeTokenName = (hexToken: string, index: number): string => {
    try {
      const assetNameHex = hexToken.substring(56);
      if (!assetNameHex) return `Token ${index + 1}`;
      const bytes = new Uint8Array(
        assetNameHex.match(/.{1,2}/g)!.map((b) => parseInt(b, 16))
      );
      const decoded = new TextDecoder().decode(bytes);
      if (decoded) return decoded;
    } catch (e) {}
    return `Token ${index + 1}`;
  };

  const decodeFundName = (hexName: string): string => {
    try {
      let str = "";
      for (let i = 0; i < hexName.length; i += 2) {
        const charCode = parseInt(hexName.substring(i, i + 2), 16);
        if (charCode === 0) break;
        str += String.fromCharCode(charCode);
      }
      return str;
    } catch (e) {
      return "";
    }
  };

  const fundDisplayName = fund.fund_name || decodeFundName(fund.name) || "this fund";

  const getCardanoscanTokenUrl = (hexToken: string): string => {
    const baseUrl = CARDANO_NETWORK === NetworkType.MAINNET
      ? "https://cardanoscan.io"
      : "https://preprod.cardanoscan.io";
    const policyId = hexToken.substring(0, 56);
    const assetNameHex = hexToken.substring(56);
    return `${baseUrl}/token/${policyId}.${assetNameHex}`;
  };

  const getMuesliSwapBuyTokenUrl = (hexToken: string): string => {
    const policyId = hexToken.substring(0, 56);
    const assetNameHex = hexToken.substring(56);
    return `https://v2.muesliswap.com/?buy_token=${policyId}.${assetNameHex}&sell_token=.`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const multipleNum = parseInt(multiple, 10);
    if (isNaN(multipleNum) || multipleNum < 1) {
      return;
    }

    setLoading(true);
    try {
      await onDeposit(multipleNum);
      onClose();
      setMultiple("");
    } catch (error) {
      console.error("Deposit failed:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
            {t("fund-details.depositModal.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Icon
              icon={ICON_COLLECTION.cross}
              iconSize="24px"
              iconColor={theme === "dark" ? "#9CA3AF" : "#6B7280"}
            />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("fund-details.depositModal.multiple")}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={multiple}
              onChange={(e) => setMultiple(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="Enter amount"
            />
            {multiple !== "" && (!Number.isInteger(Number(multiple)) || multiple.includes(".")) && (
              <p className="text-xs text-red-400 mt-1">Can only be an integer</p>
            )}
          </div>

          {/* Token balances breakdown */}
          {enabledWallet && fund.tokens.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Token Balances
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">
                Amount of tokens in your wallet vs. what you need
              </p>
              <div className="space-y-1">
                {fund.tokens.map((hexToken, index) => {
                  const tokenName = decodeTokenName(hexToken, index);
                  const tokenId = hexToken.toLowerCase();
                  const walletAmount = tokenBalances.get(tokenId) ?? BigInt(0);
                  const needed = requiredAmounts?.[index] ?? null;
                  const sufficient = needed === null || walletAmount >= needed;
                  const decimals = getTokenDecimals(tokenId);
                  const walletDisplay = formatTokenAmount(walletAmount, decimals);
                  const neededDisplay =
                    needed !== null
                      ? formatTokenAmount(needed, decimals)
                      : null;

                  return (
                    <div
                      key={hexToken}
                      className="flex justify-between text-sm"
                    >
                      <div className="flex items-center min-w-0 mr-2">
                        <a
                          href={getCardanoscanTokenUrl(hexToken)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-violet-600 dark:text-violet-400 hover:underline truncate"
                        >
                          {tokenName}
                        </a>
                        <a
                          href={getMuesliSwapBuyTokenUrl(hexToken)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-1 whitespace-nowrap text-violet-600 dark:text-violet-400 hover:underline"
                        >
                          (Buy)
                        </a>
                      </div>
                      <span
                        className={
                          needed !== null
                            ? sufficient
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                            : "text-gray-600 dark:text-gray-400"
                        }
                      >
                        {walletDisplay}
                        {neededDisplay !== null && (
                          <span className="text-gray-500 dark:text-gray-500">
                            {" "}
                            / {neededDisplay}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {shareSummary && (
            <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-800 p-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                You will receive{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {shareSummary.netShares.toLocaleString("en")}
                </span>{" "}
                shares of {fundDisplayName}.
              </p>
              {shareSummary.feeShares > 0n && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {shareSummary.feeShares.toLocaleString("en")} shares go to the fee.
                </p>
              )}
            </div>
          )}

          <div className="mb-6">
            {enabledWallet && balance !== null ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("fund-details.depositModal.balance")} {balance.toFixed(2)}{" "}
                ADA
              </p>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("fund-details.depositModal.connectWallet")}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {t("fund-details.depositModal.cancelBtn")}
            </button>
            <button
              type="submit"
              disabled={loading || !enabledWallet}
              className="flex-1 px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? "Processing..."
                : t("fund-details.depositModal.submitBtn")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
